// The parts an avatar is composed from. An avatar is metadata: one part id per
// category. Each id's index is its number on Eric's reference sheet (1-based
// there), and the shader draws the part by that index.

export const FACE_SHAPES = ['orb', 'blob', 'drop', 'flame', 'clover', 'cloud', 'comet', 'planet'] as const;
export type FaceShape = (typeof FACE_SHAPES)[number];

export const EYES = ['ovals', 'almonds', 'arcs', 'bars', 'stars', 'spirals', 'leaves', 'pixels'] as const;
export type Eyes = (typeof EYES)[number];

export const MOUTHS = ['smile', 'wave', 'zigzag', 'swoosh', 'open', 'smirk', 'cat', 'tilde'] as const;
export type Mouth = (typeof MOUTHS)[number];

// Hair is a small flourish on top of the head; bald is the default.
export const HAIRS = ['bald', 'tuft', 'spikes', 'bun', 'horns', 'sprout', 'antenna', 'wild'] as const;
export type Hair = (typeof HAIRS)[number];

export const ACCESSORIES = ['none', 'glasses', 'bowtie', 'monocle', 'shades', 'stars', 'blush', 'headphones'] as const;
export type Accessory = (typeof ACCESSORIES)[number];

export const AURAS = ['none', 'sparkles', 'rain', 'radiance', 'embers', 'ripples', 'orbit', 'snow'] as const;
export type Aura = (typeof AURAS)[number];

export interface AvatarConfig {
  face: FaceShape;
  eyes: Eyes;
  mouth: Mouth;
  hair: Hair;
  accessory: Accessory;
  aura: Aura;
}

// Every category, in the order the sheet lists them.
export const CATEGORIES = { face: FACE_SHAPES, eyes: EYES, mouth: MOUTHS, hair: HAIRS, accessory: ACCESSORIES, aura: AURAS } as const;

export const pick = <T>(list: readonly T[]): T => list[Math.floor(Math.random() * list.length)] as T;

// Everyone starts as option 1 of every category.
export const DEFAULT_CONFIG: AvatarConfig = { face: 'orb', eyes: 'ovals', mouth: 'smile', hair: 'bald', accessory: 'none', aura: 'none' };

// Read a stored avatar: anything missing or unknown falls back to the default,
// so old rows keep working as parts are added. Safe on the server (no DOM).
export function parseConfig(x: unknown): AvatarConfig {
  const src = (typeof x === 'object' && x !== null ? x : {}) as Record<string, unknown>;
  const out: Record<string, string> = { ...DEFAULT_CONFIG };
  for (const [k, list] of Object.entries(CATEGORIES)) {
    const v = src[k];
    if (typeof v === 'string' && (list as readonly string[]).includes(v)) out[k] = v;
  }
  return out as unknown as AvatarConfig;
}

// The shader's view of an avatar: part indices, identity (face, eyes, mouth)
// and style (hair, accessory, aura), for two ivec3 uniforms.
export const partIndices = (c: AvatarConfig): { identity: [number, number, number]; style: [number, number, number] } => ({
  identity: [FACE_SHAPES.indexOf(c.face), EYES.indexOf(c.eyes), MOUTHS.indexOf(c.mouth)],
  style: [HAIRS.indexOf(c.hair), ACCESSORIES.indexOf(c.accessory), AURAS.indexOf(c.aura)],
});

export const randomConfig = (): AvatarConfig => ({ face: pick(FACE_SHAPES), eyes: pick(EYES), mouth: pick(MOUTHS), hair: pick(HAIRS), accessory: pick(ACCESSORIES), aura: pick(AURAS) });

// Another person as the viewer sees them: the viewer's identity (face, eyes,
// mouth) living their life (hair, accessory, aura).
export const project = (viewer: AvatarConfig, other: AvatarConfig): AvatarConfig =>
  ({ ...viewer, hair: other.hair, accessory: other.accessory, aura: other.aura });
